import { NoSymbolIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useEffect, useState } from "react";
import api from "constants/api";
import { useDispatch, useSelector } from "react-redux";
import { useRef } from "react";
import {
  getList as onGetList,
  getData as onGetData,
  clearData,
} from "store/AppMaster/actions";
import { RootState } from "store/reducers";
import { useCart } from "context/CartContext";
import { SkewLoader } from "react-spinners"; // Add this import

// Define the type for a cart item based on API response
import { applyMoq, normalizeMoq } from "utils/moq";

interface CartItem {
  code: string;
  price: number;
  weight: number;
  quantity_on_hand: number;
  category: {
    id: number;
    display_name: string;
  };
  discount: number;
  quantity: number;
  GST: number;
  no_lead_day: number;
  name: string;
  project_no?: string;
  special_discount?: boolean;
  moq?: number;
}
interface DeliveryPartner {
  id: string; // or number if your IDs are numeric
  name: string;
  price: number; // rate per kg
}

const cartPageInputStyle = `
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
`;

// How long typing must pause before a qty is written to the cart. Generous, because
// a below-MOQ value is only ever a half-typed number while the field still has focus.
const QTY_INPUT_DEBOUNCE_MS = 1200;

const CartPage = () => {
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const quantityRefetchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // Code of the qty field the user is currently typing in, if any. While a field
  // is focused its value is treated as half-typed and the MOQ clamp is deferred.
  const activeQtyCodeRef = useRef<string | null>(null);
  // Codes whose qty the user has actually edited, so blur doesn't fire a pointless
  // cart write every time focus merely passes through an untouched field.
  const dirtyQtyRef = useRef<Set<string>>(new Set());
  // Latest cart, readable from callbacks that would otherwise close over a stale copy.
  const cartDataRef = useRef<any>(null);
  const dispatch = useDispatch();
  const [cartData, setCartData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownOptions, setDropdownOptions] = useState<DeliveryPartner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [deliveryCharge, setDeliveryCharge] = useState<number>(0);
  const [totalWeight, setTotalWeight] = useState<number>(0);
  const [deliveryPartnerError, setDeliveryPartnerError] = useState("");
  const [priceViewError, setPriceViewError] = useState(""); // New state for price view error

  // Add color for the loader
  const color = "#06163A";
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [isGettingPrice, setIsGettingPrice] = useState(false); // ← Add this state

  const {
    setCart,
    setTotalPrice,
    cart,
    // totalPrice,
    updateCart,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    handleProductTypeSelection,
    showTotalPrice,
    totalDiscount,
  } = useCart();
  const { data, loading } = useSelector((state: RootState) => ({
    data: state.AppMaster.data,
    loading: state.AppMaster.loading, // Assuming your Redux store has a loading state
  }));
  const [showModal, setShowModal] = useState(false);
  const [filterData, setFilterData] = useState({
    start: 0,
    limit: 10,
    search: '',
    sort_by: "_id",
    sort_order: "desc",
  });
  // const [deliveryPartnerError, setDeliveryPartnerError] = useState("");
  const [projectNumbers, setProjectNumbers] = useState<{ [code: string]: string }>({});
  // const [dropdownOptions, setDropdownOptions] = useState<{ id: string; name: string; price?: number }[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>("");
  // const [deliveryCharge, setDeliveryCharge] = useState<number>(0);
  const navigate = useNavigate();
  // Modal states for discount request
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [currentItemCode, setCurrentItemCode] = useState<string>('');
  const getCartData = async (silent: boolean = false) => {
    if (!silent) setIsLoading(true);
    const email = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
    const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + api.frontEndCartData, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, refresh_stock: true }),
      });
      const result = await response.json();
      if (response.status === 401) {
        console.error("Unauthorized: Invalid or missing token");
        navigate("/login");
        return;
      }
      if (result.statusCode === 200 && result.data.cart && result.data.cart.length > 0) {
        let cartItems = result.data.cart[0].detailed_codes || [];

        // Don't let a refresh landing mid-edit overwrite the digits the user is still
        // typing; that field is reconciled with the server when it loses focus.
        const editingCode = activeQtyCodeRef.current;
        if (editingCode && dirtyQtyRef.current.has(editingCode)) {
          const localItem = (cartDataRef.current?.detailed_codes || []).find(
            (item: CartItem) => item.code === editingCode
          );
          if (localItem) {
            cartItems = cartItems.map((item: CartItem) =>
              item.code === editingCode ? { ...item, quantity: localItem.quantity } : item
            );
          }
        }

        const count = result.data.cart[0].count || 0;
        const deliveryCharges = result.data.cart[0].delivery_charges || 0;

        const summary = calculateCartSummary(cartItems, deliveryCharges); // Include deliveryCharges
        setCart(cartItems, count);
        setCartData({ ...result.data.cart[0], ...summary, delivery_charges: deliveryCharges, total: summary.total }); // merge delivery_charges
        localStorage.setItem("cartCount", count.toString());
        setTotalWeight(summary.totalWeight || 0);
        localStorage.setItem("cartCount", count.toString());
      } else {
        setCart([], 0);
        setCartData(null);
        setTotalWeight(0);
        localStorage.setItem("cartCount", "0");
      }
    } catch (error) {
      console.error("Failed to fetch cart data:", error);
      setCart([], 0);
      setCartData(null);
      setTotalWeight(0);
      localStorage.setItem("cartCount", "0");
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    getCartData();
  }, []);

  useEffect(() => {
    cartDataRef.current = cartData;
  }, [cartData]);

  // Remove item from cart
  const handleRemoveFromCart = async (e: any, code: string) => {
    e?.preventDefault();
    // Cart contents changed -> hide prices and force a fresh Get Price click.
    setShowDetails(false);
    try {
      const email = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
      const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
      const currentCart = cartData?.detailed_codes || [];

      // Filter out the item to be removed
      const updatedDetailedCodes = currentCart.filter((item: CartItem) => item.code !== code);

      const requestData = {
        email,
        detailed_codes: updatedDetailedCodes
      };

      // Use fetch instead of dispatch
      const response = await fetch(process.env.REACT_APP_API_URL + api.frontEndUpdateCart, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        // Refresh cart data after update
        getCartData();
      }
    } catch (error) {
      console.error("Error removing item from cart:", error);
    }
  };

  const calculateCartSummary = (items: CartItem[], deliveryCharges: number = 0) => {
    let subtotal = 0;
    let totalWeight = 0;
    let totalDiscount = 0;
    let totalProductGST = 0;

    items.forEach((item) => {
      const quantity = item.quantity || 1;

      const itemSubtotal = item.price * quantity;
      const itemDiscount = (itemSubtotal * (item.discount || 0)) / 100;
      const taxableAmount = itemSubtotal - itemDiscount;

      // Extract numeric GST value from string (5% GST → 5, 18% IGST → 18)
      let gstPercent = 0;
      if (item.GST) {
        const match = String(item.GST).match(/(\d+(\.\d+)?)/);
        gstPercent = match ? parseFloat(match[0]) : 0;
      }

      const itemGST = (taxableAmount * gstPercent) / 100;

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
      totalProductGST += itemGST;
      totalWeight += (item.weight || 0) * quantity;
    });

    // GST on delivery charges (keep 18% or modify based on your rule)
    const deliveryGST = deliveryCharges * 0.18;

    const totalGST = totalProductGST + deliveryGST;

    // Match backend logic: round final total
    const totalBeforeRound = subtotal - totalDiscount + totalGST + deliveryCharges;
    const total = Math.round(totalBeforeRound);
    const roundOffValue = (total - totalBeforeRound).toFixed(2);

    return {
      subtotal,
      totalWeight,
      totalDiscount,
      totalGST,
      totalProductGST,
      deliveryGST,
      total,
      totalBeforeRound,
      roundOffValue,
    };
  };


  // Increase quantity
  const handleIncreaseQuantity = async (e: any, code: string) => {
    e.preventDefault();
    // Cart contents changed -> hide prices and force a fresh Get Price click.
    setShowDetails(false);

    // Step 1: Optimistic update (instant UI update)
    setCartData((prev: any) => {
      if (!prev) return prev;

      const updatedItems = prev.detailed_codes
        .map((item: CartItem) =>
          item.code === code
            ? { ...item, quantity: applyMoq((item.quantity || 1) + 1, item.moq) }
            : item
        )
        .filter((item: CartItem) => item.quantity > 0);

      const deliveryCharges = prev.delivery_charges || 0;
      const summary = calculateCartSummary(updatedItems, deliveryCharges);

      return {
        ...prev,
        detailed_codes: updatedItems,
        ...summary,
        totalWeight: summary.totalWeight,
      };
    });

    try {
      const email = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
      const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
      const currentCart = cartData?.detailed_codes || [];

      // Prepare updated payload
      const updatedDetailedCodes = currentCart.map((item: CartItem) =>
        item.code === code
          ? { ...item, quantity: applyMoq((item.quantity || 1) + 1, item.moq) }
          : item
      );

      const requestData = { email, detailed_codes: updatedDetailedCodes };

      const response = await fetch(process.env.REACT_APP_API_URL + api.frontEndUpdateCart, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        // Rollback if API fails
        getCartData();
      } else {
        // Settle: refetch once quantity clicks stop, so discount tiers stay in sync.
        // Silent - runs in the background, no full-page loader/content unmount.
        if (quantityRefetchDebounceRef.current) clearTimeout(quantityRefetchDebounceRef.current);
        quantityRefetchDebounceRef.current = setTimeout(() => {
          getCartData(true);
        }, 400);
      }
    } catch (error) {
      console.error("Error increasing quantity:", error);
      getCartData(); // rollback
    }
  };

  // Decrease quantity
  const handleDecreaseQuantity = async (e: any, code: string) => {
    e.preventDefault();
    // Cart contents changed -> hide prices and force a fresh Get Price click.
    setShowDetails(false);

    // Step 1: Optimistic update
    setCartData((prev: any) => {
      if (!prev) return prev;

      const updatedItems = prev.detailed_codes
        .map((item: CartItem) =>
          // Holds at MOQ rather than going below it; the line is removed with "Remove".
          item.code === code
            ? { ...item, quantity: applyMoq(item.quantity - 1, item.moq) }
            : item
        )
        .filter((item: CartItem) => item.quantity > 0);

      const deliveryCharges = prev.delivery_charges || 0;
      const summary = calculateCartSummary(updatedItems, deliveryCharges);

      return {
        ...prev,
        detailed_codes: updatedItems,
        ...summary,
        totalWeight: summary.totalWeight,
      };
    });

    try {
      const email = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
      const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
      const currentCart = cartData?.detailed_codes || [];

      const updatedDetailedCodes = currentCart
        .map((item: CartItem) =>
          item.code === code
            ? { ...item, quantity: applyMoq(item.quantity - 1, item.moq) }
            : item
        )
        .filter((item: CartItem) => item.quantity > 0);

      const requestData = { email, detailed_codes: updatedDetailedCodes };

      const response = await fetch(process.env.REACT_APP_API_URL + api.frontEndUpdateCart, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        // Rollback if API fails
        getCartData();
      } else {
        // Settle: refetch once quantity clicks stop, so discount tiers stay in sync.
        // Silent - runs in the background, no full-page loader/content unmount.
        if (quantityRefetchDebounceRef.current) clearTimeout(quantityRefetchDebounceRef.current);
        quantityRefetchDebounceRef.current = setTimeout(() => {
          getCartData(true);
        }, 400);
      }
    } catch (error) {
      console.error("Error decreasing quantity:", error);
      getCartData(); // rollback
    }
  };

  // Writes a settled quantity to the cart: applies the MOQ floor, updates the UI and
  // syncs to the server. Called once the user has finished with the field, never
  // mid-keystroke -- see handleQuantityInput.
  const commitQuantity = async (code: string, rawValue: string) => {
    const currentCart = cartDataRef.current?.detailed_codes || [];
    const targetItem = currentCart.find((item: CartItem) => item.code === code);

    const typed = parseInt(rawValue, 10);
    // A blank or invalid field falls back to 1, which the MOQ then raises to the floor.
    const newQuantity = applyMoq(Number.isFinite(typed) && typed > 0 ? typed : 1, targetItem?.moq);

    dirtyQtyRef.current.delete(code);

    setCartData((prev: any) =>
      prev
        ? {
            ...prev,
            detailed_codes: prev.detailed_codes.map((item: CartItem) =>
              item.code === code ? { ...item, quantity: newQuantity } : item
            ),
          }
        : prev
    );

    try {
      const email = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
      const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");

      const updatedDetailedCodes = currentCart.map((item: CartItem) =>
        item.code === code ? { ...item, quantity: newQuantity } : item
      );

      const response = await fetch(
        process.env.REACT_APP_API_URL + api.frontEndUpdateCart,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email, detailed_codes: updatedDetailedCodes }),
        }
      );

      if (response.ok) {
        getCartData();
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleQuantityFocus = (code: string) => {
    activeQtyCodeRef.current = code;
  };

  // The MOQ floor is applied here rather than while typing. Reaching an MOQ of 10 or
  // 20 takes at least two keystrokes, so clamping a half-typed "1" or "2" would
  // overwrite the digit the user was in the middle of entering.
  const handleQuantityBlur = (e: React.FocusEvent<HTMLInputElement>, code: string) => {
    if (activeQtyCodeRef.current === code) activeQtyCodeRef.current = null;
    if (!dirtyQtyRef.current.has(code)) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    commitQuantity(code, e.target.value);
  };

  // Direct quantity input
  const handleQuantityInput = (e: React.ChangeEvent<HTMLInputElement>, code: string) => {
    let value = e.target.value;
    // Cart contents changed -> hide prices and force a fresh Get Price click.
    setShowDetails(false);

    // If value is "0", set it to "1"
    if (value === "0") {
      value = "1";
    }

    dirtyQtyRef.current.add(code);

    // Update UI immediately (even if empty)
    setCartData((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        detailed_codes: prev.detailed_codes.map((item: CartItem) =>
          item.code === code ? { ...item, quantity: value === "" ? "" : parseInt(value, 10) } : item
        )
      };
    });

    // Debounce API call only if it's a valid number
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const typedQuantity = parseInt(value, 10);
      if (isNaN(typedQuantity) || typedQuantity < 1) return;

      const currentCart = cartDataRef.current?.detailed_codes || [];
      const targetItem = currentCart.find((item: CartItem) => item.code === code);
      const moq = normalizeMoq(targetItem?.moq);

      // Still in the field and below the MOQ: the number is most likely unfinished
      // (an MOQ-20 product cannot be reached in one keystroke). Hold off entirely and
      // let blur settle it, so the user gets as long as they need to finish typing.
      if (activeQtyCodeRef.current === code && moq > 0 && typedQuantity < moq) return;

      commitQuantity(code, String(typedQuantity));
    }, QTY_INPUT_DEBOUNCE_MS);
  };


  const handleProjectNoChange = (e: React.ChangeEvent<HTMLInputElement>, code: string) => {
    const value = e.target.value;
    setProjectNumbers(prev => ({ ...prev, [code]: value }));
  };

  // Direct project no input
  const handleProjectNoBlur = async (code: string) => {
    const newProjectNo = projectNumbers[code];
    if (!newProjectNo) return;

    try {
      const email = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
      const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
      const currentCart = cartData?.detailed_codes || [];

      const updatedDetailedCodes = currentCart.map((item: CartItem) =>
        item.code === code ? { ...item, project_no: newProjectNo } : item
      );

      const requestData = {
        email,
        detailed_codes: updatedDetailedCodes
      };

      const response = await fetch(process.env.REACT_APP_API_URL + api.frontEndUpdateCart, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        getCartData();
      }
    } catch (error) {
      console.error("Error updating project no:", error);
    }
  };
  // Handle discount request
  const handleDiscountRequest = async (code: string) => {
    const customerDetails = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}");
    const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
    const email = customerDetails.email;

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + api.specialDiscount,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email, code }),
        }
      );

      const result = await response.json();

      if (response.ok && result.statusCode === 200) {
        setModalMessage(
          `Your request for more discount regarding product ${code} has been forwarded to Our sales team and We will get back to you shortly.`
        );
      } else {
        setModalMessage(
          `Failed to send discount request for ${code}. Please try again later.`
        );
      }
    } catch (error) {
      console.error("Error sending special discount request:", error);
      setModalMessage("Something went wrong while sending your request.");
    }

    setCurrentItemCode(code);
    setShowDiscountModal(true);
  };

  // Handle All Product Email
  const handleGetPrice = async () => {
    if (showDetails) {
      // If already shown, just hide details (no API call)
      setShowDetails(false);
      return;
    }

    setShowDetails(true);
    setIsGettingPrice(false); // ← Start loader
    setPriceViewError("");    // Clear any previous error

    const customerDetails = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}");
    const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
    const email = customerDetails.email;

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + api.allProductEmail,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.statusCode !== 200) {
        // Even if API fails → keep prices visible (since user already saw them)
        // Optionally show a non-blocking toast instead of error
        console.error("Get Price API failed:", result);
        // setPriceViewError("Failed to record price view."); // Optional soft error
      }
      // No need to hide details on success — already visible!
    } catch (error) {
      console.error("Error calling Get Price API:", error);
      // Keep details visible — user already clicked "Get Price"
    } finally {
      setIsGettingPrice(false); // Always hide loader
    }
  };

  useEffect(() => {
    getCartData(); // Fallback to fetch cart data if not already available
  }, []);

  useEffect(() => {
    if (data?.result) {
      setDropdownOptions(
        data.result.map((item: any) => ({
          id: item._id,
          name: item.name,
          price: item.per_weight_charge || 0,
        }))
      );
    }
  }, [data]);

  useEffect(() => {
    // Restore previously selected delivery partner from cartData
    if (cartData?.delivery_partner && dropdownOptions.length > 0) {
      const partner = dropdownOptions.find(
        (opt) => opt.name === cartData.delivery_partner
      );
      if (partner) setSelectedOption(partner.id);
    }
  }, [cartData, dropdownOptions]);

  useEffect(() => {
    const fetchDeliveryPartners = async () => {
      try {
        const token = localStorage.getItem("AuthCustomer");
        if (!token) {
          console.log("No authentication token found in localStorage.");
          return;
        }

        const url = `${process.env.REACT_APP_API_URL}${api.getDeliveryPartnerList}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token.replace(/"/g, '')}`,
          },
          body: JSON.stringify(filterData)
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const responseData = await response.json();
        // console.log("API Response for Delivery Partners:", responseData);

        if (responseData?.data?.result && Array.isArray(responseData.data.result)) {
          const options = responseData.data.result
            .filter((item: any) => item.status === "Active")
            .map((item: any) => ({
              id: item._id,
              name: item.name,
              price: item.per_weight_charge || 0,
            }));
          setDropdownOptions(options);
          // console.log("Dropdown options set:", options);
        } else {
          console.error("API did not return the expected 'data.result' array.", responseData);
          setDropdownOptions([]);
        }
      } catch (error) {
        console.error("Failed to fetch delivery partners:", error);
        setDropdownOptions([]);
      }
    };

    fetchDeliveryPartners();
  }, []);

  // Update Freight Charge instantly
  // Update this useEffect for instant Freight Charge calculation
  useEffect(() => {
    if (!selectedOption || !cartData?.detailed_codes) {
      setDeliveryCharge(0);
      return;
    }

    const partner = dropdownOptions.find(p => p.id === selectedOption);
    if (!partner) return;

    const charge = Math.ceil(totalWeight) * partner.price;
    setDeliveryCharge(charge);

    // Update cartData for UI display (no API)
    setCartData((prev: any) => {
      if (!prev) return prev;
      const summary = calculateCartSummary(prev.detailed_codes, charge);
      return {
        ...prev,
        delivery_charges: charge,
        ...summary,
      };
    });
  }, [selectedOption, totalWeight, dropdownOptions]);

  useEffect(() => {
    if (!selectedOption) return;

    const partner = dropdownOptions.find(p => p.id === selectedOption);
    if (!partner) return;

    const charge = Math.ceil(totalWeight) * partner.price;

    const updateDeliveryCharge = async () => {
      try {
        const customer = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}");
        const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");

        const requestData = {
          email: customer.email,
          detailed_codes: cartData?.detailed_codes || [],
          delivery_partner: partner.name,
          delivery_charges: charge,
        };

        await fetch(process.env.REACT_APP_API_URL + api.frontEndUpdateCart, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
        });
      } catch (error) {
        console.error("Error updating delivery partner:", error);
      }
    };

    updateDeliveryCharge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]); // <-- only run when user changes partner


  const renderContent = () => {
    if (isLoading) {
      return <p>Loading cart...</p>;
    }

    if (!cartData?.detailed_codes?.length) {
      return <p>Your cart is empty.</p>;
    }

    return cartData.detailed_codes.map(renderProduct);
  };
  // Handle checkout
  const handleCheckout = () => {
    // if (!areAllItemsInStock()) {
    //   setShowModal(true);
    //   return;
    // }
    if (!showDetails) {
      setPriceViewError("Please view the price first by clicking 'Get Price'.");
      return; // stop checkout
    }
    if (!selectedOption) {
      setDeliveryPartnerError("Please select a Delivery Partner");
      return; // stop checkout
    }
    setShowModal(false);
    window.location.href = "/checkout";
  };

  const handleDeliveryPartnerChange = async (partnerId: string) => {
    setSelectedOption(partnerId);
    setDeliveryPartnerError(""); // clear any previous errors

    if (!cartData) return;

    const selected = dropdownOptions.find(opt => opt.id === partnerId);
    if (!selected) return;

    const charge = Math.ceil(totalWeight) * selected.price;
    setDeliveryCharge(charge);

    // Prepare API payload
    const email = JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
    const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");

    const requestData = {
      email,
      detailed_codes: cartData.detailed_codes, // keep existing cart items
      delivery_partner: selected.name,
      delivery_charges: charge,
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + api.frontEndUpdateCart,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (response.ok) {
        getCartData(); // refresh cart after update
      } else {
        console.error("Failed to update delivery partner");
      }
    } catch (error) {
      console.error("Error updating delivery partner:", error);
    }
  };

  // Whenever cartData changes, update totalWeight
  useEffect(() => {
    if (cartData?.totalWeight) setTotalWeight(cartData.totalWeight);
  }, [cartData?.totalWeight]);

  // Clear price view error when showDetails is true
  useEffect(() => {
    if (showDetails) {
      setPriceViewError("");
    }
  }, [showDetails]);

  const renderStatusSoldout = (item: CartItem) => (
    <div className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-base font-bold text-red-500 ring-1 ring-red-600/10 ring-inset">
      {/* <NoSymbolIcon className="w-3.5 h-3.5" /> */}
      <span className="ml-1 leading-none fw-bold">
        {/* {item.no_lead_day ? `${item.no_lead_day} days` : "Sold Out"} */}
        Available in {item.no_lead_day} days
      </span>
    </div>
  );

  const renderStatusInstock = () => (
    <div className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-base font-bold text-green-700 ring-1 ring-green-600/20 ring-inset">
      <CheckIcon className="w-3.5 h-3.5" />
      <span className="ml-1 leading-none">In Stock</span>
    </div>
  );

  const renderProduct = (item: CartItem, index: number) => {
    const { code, price, weight, quantity, discount, category, quantity_on_hand } = item;

    // Parse stock quantities
    // const standardStock = parseInt(row.available_quantity || "0", 10);
    // const economicStock = parseInt(row.available_economic_quantity || "0", 10);

    // Check stock availability
    const isInStock = quantity_on_hand >= quantity;

    // Minimum order quantity; 0 means "no restriction" and no label is shown.
    const moq = normalizeMoq(item.moq);

    // const displayQuantity = product_type === "standard" ? quantity : econ_quantity;
    // const displayPrice = product_type === "standard" ? row.price || 0 : row.economic_price || 0;
    // const displayDiscount = product_type === "standard" ? row.discount || 0 : row.economic_discount || 0;

    return (
      <div key={index} className="relative flex p-5 items-center sm:items-start">
        <div className="ml-3 sm:ml-6 flex flex-1 flex-col">
          <div>
            <div className="flex flex-col md:flex-row justify-between">
              <div className="flex-1">
                <div className="flex gap-3">
                  <h3 className="text-base font-semibold">
                    {code}
                  </h3>
                  <div>
                    {isInStock ? renderStatusInstock() : renderStatusSoldout(item)}
                  </div>
                </div>
              </div>
              <div className="flex-none">
                <Link
                  to=""
                  onClick={(e) => handleRemoveFromCart(e, code)}
                  className="relative z-10 font-medium text-primary-6000 hover:text-primary-500 text-sm"
                >
                  Remove
                </Link>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between mt-2 items-center text-sm">
            <h3 className="text-base font-medium">
              Price: ₹ {((price || 0) * (quantity || 1)).toFixed(2)}
            </h3>
            <div className="text-center relative">
              {moq > 0 && (
                <p className="text-xs font-semibold text-primary-6000 mb-1">
                  MOQ {moq} pcs
                </p>
              )}
              <div className="flex items-center gap-2">
                <p className="text-gray-500 dark:text-slate-400">Qty</p>
                <input
                  type="number"
                  min={moq > 0 ? moq : 1}
                  value={quantity}
                  onChange={(e) => handleQuantityInput(e, item.code)}
                  onFocus={() => handleQuantityFocus(item.code)}
                  onBlur={(e) => handleQuantityBlur(e, item.code)}
                  className="w-16 border border-slate-300 dark:border-slate-700 rounded-md p-[0.10rem] text-center"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <h3 className="text-base font-medium">
              Weight: {((weight || 0) * (quantity || 1)).toFixed(3)} kg
            </h3>
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <h3 className="text-base font-medium">
              GST: {item.GST ?? 0} %
            </h3>
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <h3 className="text-base font-medium">
              Discount: {discount}%
            </h3>
            <div className="text-center relative">
              <span
                className="cursor-pointer"
                onClick={(e) => handleIncreaseQuantity(e, code)}
              >
                <AddCircleOutlineIcon />
              </span>
              <span>|</span>
              <span
                className="cursor-pointer"
                onClick={(e) => handleDecreaseQuantity(e, code)}
              >
                <RemoveCircleOutlineIcon />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="nc-CartPage">
      <Helmet>
        <title>Shopping Cart</title>
      </Helmet>

      {/* Add the SkewLoader overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
          <div className="absolute z-50">
            <SkewLoader color={color} loading={isLoading} />
          </div>
        </div>
      )}
      {/* Discount Request Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4">
            <p className="text-sm text-slate-700 mb-4">{modalMessage}</p>
            <button
              onClick={(e) => {
                handleRemoveFromCart(e, currentItemCode);
                setShowDiscountModal(false);
              }}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* NEW: Get Price Loader */}
      {isGettingPrice && (
        <div className="fixed inset-0 bg-white bg-opacity-80 z-50 flex items-center justify-center">
          <SkewLoader color={color} loading={isGettingPrice} />
        </div>
      )}
      
      <main className="container py-12 lg:pb-20 lg:pt-16">
        {/* Header */}
        <div className="mb-10">
          <h2 className="text-3xl lg:text-4xl font-bold">Shopping Cart</h2>
          <div className="mt-2 text-sm text-slate-600">
            <Link to="/">Home</Link>
            <span className="mx-1">/</span>
            <span className="font-medium text-primary-600">Shopping Cart</span>
          </div>
        </div>

        {/* Only render content when not loading */}
        {!isLoading && (
          <>
            {cartData?.detailed_codes?.length > 0 ? (
              <div className="flex flex-col lg:flex-row gap-10">
                {/* Cart Items */}
                <div className="w-full lg:w-3/5 space-y-6">
                  {/* Global Get Price / Hide Details Button */}
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => {
                        if (!showDetails) {
                          handleGetPrice(); // 👈 Call API only when showing details for the first time
                        } else {
                          setShowDetails(false); // Hide details on toggle
                        }
                      }}
                      className="px-5 py-2 border border-slate-300 text-white rounded-md text-sm font-medium bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 hover:border-slate-400 transition-all duration-200 shadow-sm"
                    >
                      {showDetails ? "Hide Details" : "Get Price"}
                    </button>
                  </div>
                  <div className="max-h-[443px] overflow-y-auto">
                    {cartData?.detailed_codes?.map((item: CartItem, index: number) => (
                      <div
                        key={index}
                        className="bg-white rounded-2xl shadow-sm border p-5 mb-3"
                      >
                        <div className="flex mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold">{item.code}</h3>
                              {item.quantity_on_hand >= item.quantity
                                ? renderStatusInstock()
                                : renderStatusSoldout(item)}
                            </div>
                          </div>
                          {/* <div className="flex-none">
                            {item.special_discount === true && (
                              <button
                                onClick={() => handleDiscountRequest(item.code)}
                                className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-700 transition-colors w-full sm:w-auto"
                              >
                                Special Price
                              </button>
                            )}
                          </div> */}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-start justify-between">
                          <div className="flex-1">
                            {/* Product Name */}
                            <p>
                              <span className="text-sm font-semibold text-slate-900">
                                {item.name}
                              </span>
                            </p>

                            {/* Show Price Details only when global toggle is ON */}
                            {showDetails && (
                              <>
                                <div
                                  className={`grid gap-4 mt-3 text-sm text-slate-600 ${item.discount > 0
                                    ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
                                    : "grid-cols-2"
                                    }`}
                                >
                                  <p>
                                    Price/Pcs:{" "}
                                    <span className="font-medium text-slate-900">
                                      ₹ {(item.price || 0).toFixed(2)}
                                    </span>
                                  </p>
                                  {item.discount > 0 && (
                                    <p>
                                      Discount:{" "}
                                      <span className="font-medium">
                                        {item.discount}%
                                      </span>
                                    </p>
                                  )}
                                  <p>
                                    Sub Total:{" "}
                                    <span className="font-medium text-slate-900">
                                      ₹ {(
                                        (item.price || 0) *
                                        (item.quantity || 0) *
                                        (1 - (item.discount || 0) / 100)
                                      ).toFixed(2)}
                                    </span>
                                  </p>
                                  <p>
                                    Weight/Pcs:{" "}
                                    <span className="font-medium">
                                      {(item.weight || 0).toFixed(3)} kg
                                    </span>
                                  </p>
                                  <p>
                                    GST:{" "}
                                    <span className="font-medium">
                                      {(() => {
                                        if (!item.GST) return "0%";
                                        const match = String(item.GST).match(/(\d+(\.\d+)?)%/);
                                        return match ? `${match[1]}` : "0%";
                                      })()}%
                                    </span>
                                  </p>
                                </div>

                                {/* Description Field */}
                                <div className="flex items-center gap-3 mt-3">
                                  <label className="text-sm font-medium text-slate-600 flex-none">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    value={
                                      projectNumbers[item.code] ??
                                      item.project_no ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      handleProjectNoChange(e, item.code)
                                    }
                                    onBlur={() => handleProjectNoBlur(item.code)} // API call only when focus leaves
                                    className="block rounded-md border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 w-full md:w-[60%] lg:w-[100%] xl:w-[65%] h-8"
                                    placeholder="Enter your Mould/Tool no here for reference"
                                  />
                                </div>
                              </>
                            )}
                          </div>

                          {/* Quantity + Remove Buttons */}
                          <div className="mt-4 sm:mt-0 flex flex-row md:flex-col items-center gap-4 md:gap-2">
                            <div className="flex flex-col items-center">
                              {normalizeMoq(item.moq) > 0 && (
                                <span className="text-xs font-semibold text-primary-6000 mb-1">
                                  MOQ: {normalizeMoq(item.moq)}
                                </span>
                              )}
                              <div className="flex items-center gap-2 border rounded-lg px-3 py-1 bg-slate-50">
                                <button onClick={(e) => handleDecreaseQuantity(e, item.code)}>
                                  <RemoveCircleOutlineIcon className="text-slate-600" />
                                </button>
                                <style>{cartPageInputStyle}</style>
                                <input
                                  type="number"
                                  min="0"
                                  value={
                                    item.quantity === undefined || item.quantity === null
                                      ? ""
                                      : item.quantity
                                  }
                                  onChange={(e) => handleQuantityInput(e, item.code)}
                                  onFocus={() => handleQuantityFocus(item.code)}
                                  onBlur={(e) => handleQuantityBlur(e, item.code)}
                                  className={`${item.quantity?.toString().length === 1
                                    ? "w-14"
                                    : item.quantity?.toString().length === 2
                                      ? "w-16"
                                      : "w-20"
                                    } text-center border-0 bg-transparent font-semibold text-slate-800 focus:outline-none`}
                                />

                                <button onClick={(e) => handleIncreaseQuantity(e, item.code)}>
                                  <AddCircleOutlineIcon className="text-primary-600" />
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleRemoveFromCart(e, item.code)}
                              className="bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500 border border-red-500/20 rounded-full transition"
                            >
                              Remove
                            </button>
                          </div>  
                        </div>
                      </div>
                    ))}
                  </div>                  
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={handleGetPrice}
                      disabled={isGettingPrice}
                      className={`px-5 py-2 border border-slate-300 text-white rounded-md text-sm font-medium transition-all duration-200 bg-slate-900 dark:bg-slate-100  shadow-sm ${isGettingPrice
                        ? "opacity-70 cursor-not-allowed"
                        : "hover:bg-slate-800 hover:border-slate-400"
                        }`}
                    >
                      {isGettingPrice ? (
                        <>Processing...</>
                      ) : showDetails ? (
                        "Hide Details"
                      ) : (
                        "Get Price"
                      )}
                    </button>
                  </div>
                </div>
                {/* Order Summary */}
                <div className="flex-1">
                  <div className="bg-white dark:bg-slate-900 shadow-md rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      {/* <i className="las la-shipping-fast text-2xl text-blue-500"></i> */}
                      Select Delivery Partner
                    </h3>
                    <select
                      id="dropdown"
                      value={selectedOption}
                      onChange={(e) => handleDeliveryPartnerChange(e.target.value)}
                      className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="" disabled>
                        Choose a Delivery Partner
                      </option>
                      {dropdownOptions.length > 0 ? (
                        dropdownOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name} (₹{option.price}/kg)
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          Loading delivery partners...
                        </option>
                      )}
                    </select>
                    {deliveryPartnerError && (
                      <p className="text-rose-600 mt-2 text-sm">{deliveryPartnerError}</p>
                    )}
                  </div>
                  <div className="bg-white dark:bg-slate-900 shadow-md rounded-2xl p-6 border border-slate-200 dark:border-slate-700 mt-4">
                    <h3 className="text-xl font-semibold mb-5">Order Summary</h3>
                    <div className="divide-y divide-slate-200 text-sm">
                      <div className="flex justify-between py-3">
                        <span>
                          {showDetails && cartData?.totalDiscount > 0
                            ? "Sub Total (Before Discount)"
                            : "Sub Total"}
                        </span>
                        <span className="font-semibold">
                          {showDetails ? `₹ ${cartData?.subtotal?.toFixed(2) || "0.00"}` : ""}
                        </span>
                      </div>
                      {/* <div className="flex justify-between py-3">
                        <span>Total Weight</span>
                        <span className="font-semibold">
                          {cartData?.totalWeight?.toFixed(2) || 0} kg
                        </span>
                      </div> */}
                      {showDetails && cartData?.discount > 0 && (
                        <>
                          <div className="flex justify-between py-3">
                            <span>Total Discount</span>
                            <span className="font-semibold text-green-600">
                              {showDetails ? `₹ ${cartData?.totalDiscount?.toFixed(2) || 0}` : ''}
                            </span>
                          </div>

                          <div className="flex justify-between py-3">
                            <span>Sub Total (After Discount)</span>
                            <span className="font-semibold">
                              {showDetails ? `₹ ${(
                                (cartData?.subtotal || 0) - (cartData?.totalDiscount || 0)
                              ).toFixed(2)}` : ''}
                            </span>
                          </div>
                        </>
                      )}

                      <div className="flex justify-between py-3">
                        <span>Freight Charge for ({showDetails ? (cartData?.totalWeight?.toFixed(3) || 0) : ''} kg)</span>
                        <span className="font-semibold">
                          {showDetails ? `₹ ${(cartData?.delivery_charges || 0).toFixed(2)}` : ''}
                        </span>
                      </div>
                      <div className="flex justify-between py-3">
                        <span>GST Charge</span>
                        <span className="font-semibold">
                          {showDetails ? `₹ ${cartData?.totalGST?.toFixed(2) || 0}` : ''}
                        </span>
                      </div>
                      <div className="flex justify-between py-3">
                        <span>Round Off. Amount</span>
                        <span className="font-semibold">
                          {showDetails ? `₹ ${cartData?.roundOffValue || "0.00"}` : ""}
                        </span>
                      </div>
                      <div className="flex justify-between py-4 text-lg font-bold">
                        <span>Order Total</span>
                        <span>{showDetails ? `₹ ${cartData?.total?.toFixed(2) || 0}` : ''}</span>
                      </div>
                    </div>
                    {priceViewError && (
                      <p className="text-rose-600 mt-4 text-sm">{priceViewError}</p>
                    )}
                    <ButtonPrimary
                      onClick={handleCheckout}
                      className="mt-6 w-full py-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 text-white text-lg font-semibold"
                    >
                      Proceed to Checkout
                    </ButtonPrimary>
                    {/* {showModal && !areAllItemsInStock() && (
                  <p className="text-red-500 text-sm mt-3">
                    Please remove sold-out products before checkout.
                  </p>
                )} */}
                  </div>
                </div>
              </div>
            ) : (
              // Empty Cart State
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/2038/2038854.png"
                  alt="Empty Cart"
                  className="w-32 h-32 mb-6 opacity-80"
                />
                <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
                <p className="text-slate-600 mb-6">
                  Looks like you haven’t added anything yet.
                </p>
                <Link
                  to="/category"
                  className="px-6 py-3 rounded-xl bg-primary-700 text-white text-lg font-medium shadow"
                >
                  Continue Shopping
                </Link>
              </div>
            )}
          </>
        )}
      </main>
    </div>

  );
};

export default CartPage;